/**
 * Cloudflare Turnstile: proves to the SalunBD API that sign-up, sign-in and booking come from a real person in a browser
 * (the server's half is salon-backend-service/src/common/integrity/turnstile.*). The browser counterpart of
 * playIntegrity.ts, which covers the Android app.
 *
 * Before one of those requests the page runs a Turnstile widget and sends the token it produces as X-Turnstile-Token. The
 * widget is invisible for almost everyone; when Cloudflare is unsure it shows a small tick box in the corner of the page.
 * Anything that goes wrong here (Cloudflare blocked by the visitor's network, no site key set) sends the request without a
 * token: the server decides what that means (it only refuses once it is switched to enforce), so this never blocks someone
 * by itself.
 *
 * Web only, and only with EXPO_PUBLIC_TURNSTILE_SITE_KEY set (the public key of the widget made at dash.cloudflare.com).
 * The Android and iOS apps never load it.
 */
import { Platform } from 'react-native';
import { routeActionFor, type IntegrityAction } from './playIntegrity';

const SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY?.trim() ?? '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** Long enough for a person to tick the box when Cloudflare asks. */
const TOKEN_TIMEOUT_MS = 60_000;
const SCRIPT_TIMEOUT_MS = 10_000;

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

let scriptLoading: Promise<TurnstileApi> | null = null;

const inBrowser = () => Platform.OS === 'web' && typeof document !== 'undefined' && typeof window !== 'undefined';

/** Which action (if any) a request to the API needs a Turnstile token for. */
export function turnstileActionFor(method: string, apiBase: string, url: string): IntegrityAction | null {
  if (!SITE_KEY || !inBrowser()) return null;
  return routeActionFor(method, apiBase, url);
}

function loadScript(): Promise<TurnstileApi> {
  scriptLoading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const win = window as unknown as { turnstile?: TurnstileApi };
    if (win.turnstile) return resolve(win.turnstile);
    const timer = setTimeout(() => reject(new Error('Turnstile took too long to load')), SCRIPT_TIMEOUT_MS);
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      clearTimeout(timer);
      if (win.turnstile) resolve(win.turnstile);
      else reject(new Error('Turnstile did not start'));
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Turnstile could not be loaded'));
    };
    document.head.appendChild(script);
  }).catch((err) => {
    scriptLoading = null; // try again on the next request
    throw err;
  });
  return scriptLoading;
}

/** The header for one request, or none when proof is off, not possible here, or failed. Never throws. */
export async function turnstileHeaders(action: IntegrityAction): Promise<Record<string, string>> {
  if (!SITE_KEY || !inBrowser()) return {};
  let widgetId: string | null = null;
  let turnstile: TurnstileApi | null = null;
  // Fixed in a corner: invisible unless Cloudflare needs the person to tick a box.
  const container = document.createElement('div');
  container.setAttribute('data-turnstile-for', action);
  container.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:2147483000;';
  try {
    turnstile = await loadScript();
    document.body.appendChild(container);
    const token = await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Turnstile took too long')), TOKEN_TIMEOUT_MS);
      const done = (fn: () => void) => {
        clearTimeout(timer);
        fn();
      };
      widgetId = turnstile!.render(container, {
        sitekey: SITE_KEY,
        action,
        appearance: 'interaction-only',
        callback: (value: string) => done(() => resolve(value)),
        'error-callback': () => done(() => reject(new Error('Turnstile reported an error'))),
        'timeout-callback': () => done(() => reject(new Error('Turnstile timed out'))),
      });
    });
    return { 'x-turnstile-token': token };
  } catch (err) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) console.warn(`[turnstile] sending without proof: ${(err as Error).message}`);
    return {};
  } finally {
    try {
      if (turnstile && widgetId) turnstile.remove(widgetId);
    } catch {
      /* the widget may already be gone */
    }
    container.remove();
  }
}
